import { Injectable } from '@angular/core';
import { JoyrideService } from 'ngx-joyride';

@Injectable()
export class HelpService {
	// joyride steps
	POs = [
		{
			id: 'step1',
			title: '',
			text: 'The "Primary" tab lists all Purchase Orders managed by you. The “Inactive” tab may be used to store inactive or expired POs. If there are less than 10% of funds or less than 40 hours remaining on a PO, the PO will be highlighted in orange. Please work with IBM Procurement to add funds or request a renewal.'
		}, {
			id: 'step2',
			title: '',
			text: 'Click on a line to view each Purchase Order in detail. Select checkboxes to perform various actions. Sort rows by selecting column titles.'
		}, {
			id: 'step3',
			title: '',
			text: 'Use the dropdown menu to select actions to perform on selected POs then click “Go”.'
		}
	];

	PO = [
		{
			id: 'step1',
			title: '',
			text: 'The “Details” tab lists PO information. The “Contractor Line Items” tab lists the line items on the PO and allows you to add contractors to them with the "Add New Contractor" button.'
		}, {
			id: 'step2',
			title: '',
			text: 'The Invoice Records tabs let you view all invoices by status. Click on a line to view each invoice in detail.'
		}, {
			id: 'step3',
			title: '',
			text: 'The Contractor Records tabs let you view all contractor records by status. Click on a line to view each contractor in detail and make edits or approvals.'
		}
	];

	timesheets = [
		{
			id: 'step1',
			title: '',
			text: 'Select each tab to view all timesheets by status. Pending timesheets are “Escalated” based upon the escalation date set by the Supplier Administrator.'
		}, {
			id: 'step2',
			title: '',
			text: 'Click on a specific line to view each timesheet in detail. Select checkboxes to perform various actions. Sort rows by selecting column titles.'
		}, {
			id: 'step3',
			title: '',
			text: 'Use the dropdown menu to select actions to perform on selected timesheets then click “Go”.'
		}
	];

	timesheet = [
		{
			id: 'TSStep1',
			title: '',
			text: "Welcome to your timesheet. Click 'Next' to follow the guided tour."
		}, {
			id: 'TSStep2',
			title: '',
			text: 'This is information about this particular timesheet.'
		}, {
			id: 'TSStep3',
			title: '',
			text: 'You can use this calendar to navigate through different weeks for timesheet claiming based on the PO start and end dates. Make sure you click the blue arrow icon after selecting the dates.'
		}, {
			id: 'TSStep4',
			title: '',
			text: "This is where you enter your timesheet line items. On the left, select the type of hours worked, or if ILC enabled, use an ILC claim code and click the ‘Search ILC’ button. If you don't know your code, please reach out to your manager. For multiple claim codes, click the ‘Add Timesheet Row’ button."
		}, {
			id: 'TSStep5',
			title: '',
			text: 'Input your hours worked in the hours boxes. Note that weeks begin on Saturdays.'
		}, {
			id: 'TSStep6',
			title: '',
			text: 'Enter any expenses here and upload a breakdown document.'
		}, {
			id: 'TSStep7',
			title: '',
			text: "Once you've input all of your hours, click 'Submit'. If you're not ready to submit your timesheet yet, click 'Save' to come back to complete it later."
		}, {
			id: 'TSStep8',
			title: '',
			text: 'That wraps up our tour! For any help you need, please go to our User Help page located in the menu for documentation and contact information for our production support team.'
		}
	]

	timesheet_tooltips = {
		overview: 'Welcome to Contingent Labor on IBM Blockchain. This page allows you to track and submit time. Hover over each help icon � to display helpful tips.',
		selectPO: 'If you claim time against several Purchase Orders, you may select a specific PO in the top right corner.',
		poDetails: 'The Purchase Order details can be found here. If any information is incorrect, please contact your Manager.',
		timeRange: 'To load a past timesheet, select a specific date and click “Go”. You will not be able to access future timesheets.',
		addTR: 'Choose the “Add Timesheet Row” option to add rows so you may claim time against multiple accounts. Use this option for claiming Over Time hours.',
		expenses: 'Expenses may be entered here. An expense breakdown file is required to submit expenses. This may come from an expense reporting tool (such as Concur) or an excel spreadsheet. Expense receipts are required for amounts $75 and above.',
		submit: 'After clicking "Submit", you will still be able to select a reason code to make edits. You may also “Save” your timesheet without submitting.',
		countrySelect: 'Timesheets have different hour types per country, so the table can not display data from multiple countries at the same time.'
	};

	timesheet_row_tooltips = {
		accounts: 'To submit time, you must first select an account to bill against.  If you are ILC-enabled, you must select an ILC account.'
	};

	contractors = [
		{
			id: 'step1',
			title: '',
			text: 'Select each tab to view all contractor records by status. Records may be pending actions by either party.'
		}, {
			id: 'step2',
			title: '',
			text: 'Click on a specific line to view each contractor in detail. Select checkboxes to perform various actions. Sort rows by selecting column titles.'
		}, {
			id: 'step3',
			title: '',
			text: 'Use the dropdown menu to select actions to perform on selected contractor records then click “Go”.'
		}
	];

	contractor = [
		{
			id: 'step1',
			title: '',
			text: 'Choose a date and click the arrow icon to view a specific timesheet'
		}, {
			id: 'step2',
			title: '',
			text: 'Choose a start date and end date to filter timesheets to view'
		}, {
			id: 'step3',
			title: '',
			text: 'Select each tab to view all or rejected timesheets for this specific contractor'
		}
	];

	contractor_tooltips = {
		details: 'Select each tab to view contractor details, apply edits, and approve edits. Edits awaiting submission will show an empty red circle. Edits awaiting approval will show a full red circle. Contractor setup is complete when both tabs have green check marks.'
	};

	invoices = [
		{
			id: 'step1',
			title: '',
			text: 'Select each tab to view all invoices by status. “Pending” invoices await automatic reconciliation. “Failed” invoices are invoices that were unable to reconcile.'
		}, {
			id: 'step2',
			title: '',
			text: 'Click on a specific line to view each invoice in detail. Select checkboxes to perform actions. Sort rows by selecting column titles.'
		}, {
			id: 'step3',
			title: '',
			text: 'Use the dropdown menu to select actions to perform on selected invoices then click “Go”.'
		}
	];

	supplier_invoices = [
		{
			id: 'step1',
			title: 'Supplier Invoices',
			text: 'Select each tab to view all supplier invoices by status. “Pending” invoices await automatic reconciliation. “Failed” invoices are invoices that were unable to reconcile.'
		}, {
			id: 'step2',
			title: '',
			text: 'Click on a specific line to update the billing start and end date if you have a permission. Select checkboxes to perform actions. Sort rows by selecting column titles.'
		}, {
			id: 'step3',
			title: '',
			text: 'Use the dropdown menu to select actions to perform on selected invoices then click “Go”.'
		}
	];

	contracts = [
		{
			id: 'step1',
			title: '',
			text: 'All tab shows hours restrictions that have been applied to POs you manage.'
		}, {
			id: 'step2',
			title: '',
			text: 'Click on a specific line to view each hours restrictions in detail. Select checkboxes to perform actions. Sort rows by selecting column titles.'
		}, {
			id: 'step3',
			title: '',
			text: 'Use the dropdown menu to select actions to perform on selected contract changes then click “Go”.'
		}
	];

	constructor (
		private readonly joyrideService: JoyrideService
	) { }

	startTour(jrSteps) {
		this.joyrideService.startTour({steps: jrSteps});
	}

}
